import { Box, Button, Stack } from '@mui/material';
import { remove } from 'ramda';
import { useTranslation } from 'react-i18next';

import { SvbrgChatMessageStatus } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { ChatMessageProps } from '~/common/types/ChatMessage';
import { ChatStep } from '~/components/ChatStep/ChatStep';
import { UploadDropZone } from '~/components/FileUpload/UploadDropZone/UploadDropZone';
import { UploadedFileList } from '~/components/FileUpload/UploadedFileList/UploadedFileList';
import { UploadErrors } from '~/components/FileUpload/UploadErrorList/UploadErrorList';
import { ProgressIndicatorAlternating } from '~/components/ProgressIndicator/ProgressIndicatorAlternating';

import { useRequirementsUploadStep } from './IntakeRequirementsUploadStep.hooks';

const createProgressIndicatorProps = (t: any) => ({
  messages: [t('progress.analyzing'), t('progress.extracting'), t('progress.structuring'), t('progress.generating')],
});

export const IntakeRequirementsUploadStep = ({ status, role, createdBy, isCompleted }: ChatMessageProps) => {
  const { t: tIntake } = useTranslation(I18nNamespace.intake);
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeRequirementsUploadStep' });

  const {
    fileIds,
    errors,
    loading,
    isUploading,
    hasRunningAnalysis,
    isFailedAnalysis,
    skipLoading,
    uploadBatch,
    handleSkip,
    setErrors,
  } = useRequirementsUploadStep();

  const disabledSkip = isCompleted || hasRunningAnalysis || skipLoading;
  const disableUpload = isCompleted || isUploading;

  return (
    <ChatStep
      title={tIntake('IntakeChatStep.assistant')}
      role={role}
      createdBy={createdBy}
      message={
        isUploading || hasRunningAnalysis || status === SvbrgChatMessageStatus.PENDING ? (
          <ProgressIndicatorAlternating {...createProgressIndicatorProps(t)} />
        ) : (
          t('message', 'Upload your requirements document to auto-populate the form (optional)')
        )
      }
      footer={
        <Button onClick={handleSkip} disabled={disabledSkip}>
          {t('button.skip')}
        </Button>
      }
    >
      {!loading && ((isFailedAnalysis && fileIds) || !fileIds || fileIds.length === 0) && !isUploading && (
        <Stack spacing={2} sx={{ paddingTop: 2 }}>
          <UploadDropZone
            onDrop={uploadBatch}
            disabled={disableUpload}
            isMultiFileUpload={true}
            accept={{
              'text/plain': ['.txt'],
              'application/pdf': ['.pdf'],
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
            }}
            buttonLabel={t('button.upload')}
            onDropErrors={(errorMessages) => setErrors(errorMessages)}
          />
          <UploadErrors errors={errors} onDiscard={(index) => setErrors(remove(index, 1))} />
        </Stack>
      )}
      {!isUploading && fileIds && !isFailedAnalysis && (
        <Box sx={{ paddingTop: 2 }}>
          <UploadedFileList files={fileIds.map((fileId) => ({ fileId }))} />
        </Box>
      )}
    </ChatStep>
  );
};
